'use client';

import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import LinkedIn from '@/assets/linkedin.svg';
import Facebook from '@/assets/facebook.svg';
import Paperclip from '@/assets/paperclip.svg';

export default function ShareBar({ className }: { className?: string }) {
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setShareUrl(window.location.href);
    }
  }, []);

  const links = useMemo(() => {
    const encoded = encodeURIComponent(shareUrl || '');
    return {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`,
    };
  }, [shareUrl]);

  function openShare(url: string) {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // noop
    }
  }

  return (
    <div className={clsx('flex items-center gap-3', className)}>
      {/* Facebook */}
      <button
        type="button"
        aria-label="Share on Facebook"
        onClick={() => openShare(links.facebook)}
        className="inline-flex h-9 w-9 items-center justify-center rounded border border-teal-500 text-teal-500 hover:bg-teal-500 hover:text-white"
      >
        {/* Facebook icon */}
        <Facebook />
      </button>

      {/* LinkedIn */}
      <button
        type="button"
        aria-label="Share on LinkedIn"
        onClick={() => openShare(links.linkedin)}
        className="inline-flex h-9 w-9 items-center justify-center rounded border border-teal-500 text-teal-500 hover:bg-teal-500 hover:text-white"
      >
        {/* LinkedIn icon */}
        <LinkedIn />
      </button>

      {/* Copy Link */}
      <button
        type="button"
        onClick={copyToClipboard}
        className="inline-flex items-center gap-2 rounded border border-teal-500 bg-white px-3 py-1.5 text-teal-500 hover:bg-teal-500 hover:text-white"
      >
        <span className="text-[14px]">{copied ? 'Copied' : 'Copy Link'}</span>
        <Paperclip />
      </button>
    </div>
  );
}
